import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { OptionPrices } from "building-types/options/optionPricing";
import type { DocOption } from "building-types/options/optionSet";
import type { Multimap } from "common/data/multimap";
import { getOptionSetArchetypeOrSelf } from "../getOptionSetArchetypeOrSelf";
import { getOptionChoicesWithDefaults } from "../optionChoicesTools";
import { optionSetDefaultOptionId } from "../optionSetBaseUtils";
import { optionPrice } from "../optionSetTools";
import {
  type ActiveIntersectionEffect,
  intersectionModifiesField,
  type SegmentedIntersectionEffects,
} from "./intersectingOptionSetShared";

type OptionalModifications = ReadonlyArray<ActiveIntersectionEffect> | null | undefined;

export function priceAdditiveIntersections(modifications: OptionalModifications): number {
  if (Array.isArray(modifications)) {
    return modifications.filter(intersectionModifiesField("price")).reduce((sum, i) => sum + optionPrice(i.option), 0);
  }
  return 0;
}

export function optionPriceWithIntersections(
  option: Pick<DocOption, "price"> | null | undefined,
  modifications: OptionalModifications,
  choicesWithDefaults?: OptionChoices,
) {
  const basePrice = optionPrice(option, choicesWithDefaults);
  const additionalPrice = priceAdditiveIntersections(modifications);
  return basePrice + additionalPrice;
}

function optionEnabled(definitions: OptionDefinitions, optionSetId: string, optionId: string | null) {
  const optionSet = getOptionSetArchetypeOrSelf(definitions, optionSetId);
  if (optionSet) {
    const option = optionId && optionSet.options[optionId];
    if (option) {
      return !optionSet.disabled && !option.disabled;
    }
  }
  return false;
}

function optionEnabledForEntry(definitions: OptionDefinitions) {
  return ([optionSetId, optionId]: [string, string | null]) => optionEnabled(definitions, optionSetId, optionId);
}

function pricesForOptionChoices(
  definitions: OptionDefinitions,
  choices: OptionChoices,
  modifications: Multimap<ActiveIntersectionEffect>,
  withIntersectionModificationsRendered: boolean,
): OptionPrices {
  const enabled = optionEnabledForEntry(definitions);
  const choicesWithDefaults = getOptionChoicesWithDefaults(definitions, choices);
  return Object.fromEntries(
    Object.entries(choices)
      .filter(enabled)
      .map(([optionSetId, optionId]) => {
        const optionSet = getOptionSetArchetypeOrSelf(definitions, optionSetId);
        const option = optionId !== null && optionSet ? optionSet.options[optionId] : undefined;
        // Lookup modifications by archetype ID since that's how modifications is keyed
        const archetypeId = optionSet?.id ?? optionSetId;
        const price = withIntersectionModificationsRendered
          ? optionPriceWithIntersections(option, modifications[archetypeId], choicesWithDefaults)
          : optionPrice(option, choicesWithDefaults);
        return [optionSetId, { optionId, price }];
      }),
  );
}

/**
 * If there are modifying intersections missing from option choices, they were triggered by option defaults
 * We need to add them to the option choices to calculate the correct price
 */
function modifiedBaseOptionChoices(
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
  modifications: Multimap<ActiveIntersectionEffect>,
  withIntersectionModificationsRendered: boolean,
): OptionPrices {
  const modifiedBaseOptionChoices: OptionChoices = Object.fromEntries(
    Object.keys(modifications)
      .filter((optionSetId) => !optionChoices[optionSetId])
      .map((optionSetId) => [
        optionSetId,
        optionSetDefaultOptionId(getOptionSetArchetypeOrSelf(definitions, optionSetId) ?? null, definitions.optionSets),
      ]),
  );
  return Object.fromEntries(
    Object.entries(
      pricesForOptionChoices(
        definitions,
        modifiedBaseOptionChoices,
        modifications,
        withIntersectionModificationsRendered,
      ),
    ).filter(([, { price }]) => Boolean(price)),
  );
}

/**
 * When an intersection generates an option, we need to add it to the option prices
 */
function generatedIntersections(
  definitions: OptionDefinitions,
  intersections: SegmentedIntersectionEffects,
): OptionPrices {
  return Object.fromEntries(
    intersections.generate
      .filter(({ option }) => optionEnabled(definitions, option.parentId, option.id))
      .map(({ option }) => [option.parentId, { optionId: option.id, price: optionPrice(option) }]),
  );
}

export function getOptionPrices(
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
  intersections: SegmentedIntersectionEffects,
  withIntersectionModificationsRendered: boolean,
): OptionPrices {
  return {
    ...pricesForOptionChoices(definitions, optionChoices, intersections.modify, withIntersectionModificationsRendered),
    ...modifiedBaseOptionChoices(
      definitions,
      optionChoices,
      intersections.modify,
      withIntersectionModificationsRendered,
    ),
    ...generatedIntersections(definitions, intersections),
  };
}
